import create from 'zustand-vue'
import { devErrLog, devLog, devWarnLog } from '../utils/logger'

// LocalStorage key for persistent session (single item)
const STORAGE_KEY = 'sixshot_auth_session'

// Helper functions for localStorage
const saveToLocalStorage = (user, isAuthenticated) => {
	try {
		if (user && isAuthenticated) {
			const sessionData = {
				user,
				isAuthenticated,
				timestamp: Date.now()
			}
			localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData))
			devLog('✅ Session saved to localStorage')
		}
	} catch (error) {
		devErrLog('Failed to save to localStorage:', error)
	}
}

const loadFromLocalStorage = () => {
	try {
		const sessionStr = localStorage.getItem(STORAGE_KEY)

		if (sessionStr) {
			const sessionData = JSON.parse(sessionStr)
			devLog('✅ Session restored from localStorage', {
				timestamp: new Date(sessionData.timestamp).toLocaleString()
			})
			return {
				user: sessionData.user,
				isAuthenticated: sessionData.isAuthenticated
			}
		}
	} catch (error) {
		devErrLog('Failed to load from localStorage:', error)
	}
	return { user: null, isAuthenticated: false }
}

const clearLocalStorage = () => {
	try {
		localStorage.removeItem(STORAGE_KEY)
		// Explicitly clear the initial redirect key first
		sessionStorage.removeItem('app_initial_redirect_performed')
		sessionStorage.removeItem('redirectAfterLogin')
		sessionStorage.removeItem('initialSelectionCheckDone')
		sessionStorage.clear()
		devLog('🗑️ Session cleared from localStorage and sessionStorage')
	} catch (error) {
		devErrLog('Failed to clear localStorage/sessionStorage:', error)
		try {
			sessionStorage.removeItem('app_initial_redirect_performed')
			sessionStorage.removeItem('redirectAfterLogin')
			sessionStorage.removeItem('initialSelectionCheckDone')
		} catch (fallbackError) {
			devErrLog('Failed to clear individual sessionStorage keys:', fallbackError)
		}
	}
}

const useAuthStore = create((set, get) => ({
	isAuthenticated: false,
	user: null,
	loading: false,
	error: null,
	sessionTimeoutId: null,
	sessionCheckInterval: null,
	PriceFundAmount: '0000',
	PriceFundLabel: '',
	countDownStatus: false,
	countDownWeek: null,
	currentWeek: null,
	nextWeekDetails: null,
	startYear: null,
	endYear: null,
	// Actions
	setLoading: (loading) => {
		set({ loading })
	},

	setError: (error) => {
		set({ error })
	},

	setUser: (user) => {
		if (!user) {
			const { logout } = get()
			logout()
			return
		}
		const isAuthenticated = !!user && !!user.profile_verified

		if (user && !user.profile_verified) {
			devWarnLog('🚨 SECURITY AUDIT: User data provided but not profile_verified', {
				userEmail: user.email,
				profileVerified: user.profile_verified,
				timestamp: new Date().toISOString(),
				action: 'unverified_user_rejected'
			})
		}

		if (isAuthenticated) {
			saveToLocalStorage(user, isAuthenticated)
		}

		set({
			user: isAuthenticated ? user : null,
			isAuthenticated,
			error: null
		})
	},

	setStartYear: (startYear) => {
		set({ startYear })
	},
	setEndYear: (endYear) => {
		set({ endYear })
	},

	login: (userData) => {
		const { startSessionMonitoring } = get()
		const isAuthenticated = !!userData && !!userData.profile_verified

		if (isAuthenticated) {
			saveToLocalStorage(userData, isAuthenticated)
		}

		set({
			user: isAuthenticated ? userData : null,
			isAuthenticated,
			loading: false,
			error: null
		})
		if (isAuthenticated) {
			startSessionMonitoring()
		}
	},

	logout: () => {
		try {
			document.cookie = 'user_email=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'

			clearLocalStorage()

			const { sessionCheckInterval, sessionTimeoutId } = get()

			if (sessionCheckInterval) {
				devLog('Stopping session monitoring, clearing interval:', sessionCheckInterval)
				clearInterval(sessionCheckInterval)
			}
			if (sessionTimeoutId) {
				devLog('Clearing session timeout:', sessionTimeoutId)
				clearTimeout(sessionTimeoutId)
			}

			set({
				user: null,
				isAuthenticated: false,
				loading: false,
				error: null,
				sessionCheckInterval: null,
				sessionTimeoutId: null
			})
		} catch (error) {
			devLog('Logout completed (with internal state update)')
		}
	},

	checkAuth: async () => {
		set({ loading: true })

		try {
			const storedSession = loadFromLocalStorage()
			if (storedSession.user && storedSession.isAuthenticated) {
				devLog('🔄 Restoring session from localStorage')
				set({
					user: storedSession.user,
					isAuthenticated: storedSession.isAuthenticated,
					loading: true
				})
			}

			const userEmail = document.cookie
				.split('; ')
				.find(row => row.startsWith('user_email='))
				?.split('=')[1]

			if (userEmail || storedSession.isAuthenticated) {
				try {
					const response = await fetch('/users/', {
						method: 'GET',
						credentials: 'include',
						headers: {
							'Content-Type': 'application/json',
							'X-CSRF-Token': window.csrfToken
						}
					})

					if (response.ok) {
						const userData = await response.json()
						if (!userData) {
							const { logout } = get()
							logout()
							return
						}
						const { startSessionMonitoring } = get()
						const isAuthenticated = !!userData && !!userData.profile_verified

						if (isAuthenticated) {
							saveToLocalStorage(userData, isAuthenticated)
						}

						set({
							user: isAuthenticated ? userData : null,
							isAuthenticated,
							loading: false,
							error: null
						})
						if (isAuthenticated) {
							startSessionMonitoring()
						}
					} else if (response.status === 401 || response.status === 403) {
						devLog('Session expired, clearing authentication')
						document.cookie = 'user_email=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
						clearLocalStorage()
						set({
							user: null,
							isAuthenticated: false,
							loading: false,
							error: 'Session expired. Please log in again.'
						})
					} else if (response.status >= 500) {
						devErrLog('Server error during authentication check:', response.status)
						devWarnLog('🚨 SECURITY AUDIT: Server error occurred while user cookie exists', {
							status: response.status,
							userEmail: decodeURIComponent(userEmail),
							timestamp: new Date().toISOString(),
							action: 'server_error_with_cookie_keeping_session'
						})
						set({
							loading: false,
							error: 'Server temporarily unavailable. Retrying...'
						})
					} else {
						devErrLog('Client error during authentication check:', response.status)
						document.cookie = 'user_email=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
						clearLocalStorage()
						set({
							user: null,
							isAuthenticated: false,
							loading: false,
							error: 'Authentication failed. Please log in again.'
						})
					}
				} catch (networkError) {
					devErrLog('Network error during authentication check:', networkError)
					set({
						loading: false,
						error: 'Network error. Please check your connection.'
					})
				}
			} else {
				set({
					user: null,
					isAuthenticated: false,
					loading: false,
					error: null
				})
			}
		} catch (error) {
			devErrLog('Unexpected error during authentication check:', error)
			set({
				loading: false,
				error: 'An unexpected error occurred.'
			})
		}
	},

	clearAuth: () => {
		const { stopSessionMonitoring } = get()
		clearLocalStorage()
		stopSessionMonitoring()
		set({
			user: null,
			isAuthenticated: false,
			loading: false,
			error: null
		})
	},

	startSessionMonitoring: () => {
		const { stopSessionMonitoring, validateSession, sessionCheckInterval } = get()

		if (sessionCheckInterval) {
			devLog('Session monitoring already active, skipping new interval creation')
			return
		}

		stopSessionMonitoring()

		const intervalId = setInterval(() => {
			validateSession()
		}, 2 * 60 * 1000)

		devLog('Session monitoring started with interval:', intervalId)
		set({ sessionCheckInterval: intervalId })
	},

	stopSessionMonitoring: () => {
		const { sessionCheckInterval, sessionTimeoutId } = get()

		if (sessionCheckInterval) {
			devLog('Stopping session monitoring, clearing interval:', sessionCheckInterval)
			clearInterval(sessionCheckInterval)
		}
		if (sessionTimeoutId) {
			devLog('Clearing session timeout:', sessionTimeoutId)
			clearTimeout(sessionTimeoutId)
		}

		set({
			sessionCheckInterval: null,
			sessionTimeoutId: null
		})
	},

	validateSession: async () => {
		try {
			const response = await fetch('/users/', {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': window.csrfToken
				}
			})

			if (response.ok) {
				const userData = await response.json()
				if (!userData) {
					const { logout } = get()
					devLog('No user data received during validation, logging out')
					logout()
				} else {
					const isAuthenticated = !!userData && !!userData.profile_verified

					if (isAuthenticated) {
						saveToLocalStorage(userData, isAuthenticated)
					}

					set({
						user: isAuthenticated ? userData : null,
						isAuthenticated
					})
				}
			} else if (response.status === 401 || response.status === 403) {
				const { logout } = get()
				devLog('Session expired during validation, logging out')
				logout()
			} else if (response.status >= 500) {
				devErrLog('Server error during session validation:', response.status)
				devLog('Keeping session active despite server error')
			} else {
				devErrLog('Client error during validation:', response.status)
				devLog('Keeping session active, may be temporary issue')
			}
		} catch (error) {
			devErrLog('Session validation network error:', error)
			devLog('Keeping session active despite network error')
		}
	},

	handleSessionExpiry: () => {
		const { logout } = get()
		devLog('Session timeout reached, auto-logging out')
		logout()
	},
	checkCountDown: async () => {
		try {
			const response = await fetch('/fixtures/countdown_to_next_gameweek', {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': window.csrfToken
				}
			});

			if (!response.ok) throw new Error('Network response was not ok');

			const data = await response.json();
			devLog("🚀 ~ data:", data);
			const countDownStatus = data && data?.time_diff_seconds;
			const countDownWeek = data && data?.game_week?.week_number;
			set({ countDownStatus, countDownWeek })
		} catch (error) {
			devErrLog('Failed to fetch deadline time:', error);
		}
	},
	checkNextWeekDetails: async () => {
		try {
			const response = await fetch('/fixtures/next_week_countdown_info', {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': window.csrfToken
				}
			});

			if (!response.ok) throw new Error('Network response was not ok');

			const data = await response.json();
			devLog("🚀 ~ data week:", data);

			if (data?.status === 'success' && data?.game_week?.week_number && data?.countdown_start?.datetime) {
				set({ nextWeekDetails: data })
			} else if (data?.banner_message) {
				set({ nextWeekDetails: { status: data.status, banner_message: data.banner_message } })
			} else {
				set({ nextWeekDetails: null })
			}
		} catch (error) {
			devErrLog('Failed to fetch NextWeekDetails time:', error);
		}
	},
	getWeekly_prize_info: async () => {
		try {
			const response = await fetch('/leaderboard/weekly_prize_info', {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': window.csrfToken
				}
			});

			if (!response.ok) throw new Error('Network response was not ok');

			const data = await response.json();
			devLog("🚀 ~ data:", data);
			const totalPrizeRaw = data?.prize_info?.total_prize_amount;

			// Convert to a number first
			const totalPrize = Number(totalPrizeRaw);

			const formattedPrize = !isNaN(totalPrize) ? totalPrize.toFixed(0) : "0";
			const PriceFundAmount = formattedPrize;
			const PriceFundLabel = `GAMEWEEK ${data?.game_week?.week_number} PRIZE FUND`;
			const currentWeek = data?.game_week?.week_number;

			set({ PriceFundAmount, PriceFundLabel, currentWeek });
		} catch (error) {
			devErrLog('Failed to fetch deadline time:', error);
		}
	},
	getYear: async () => {
		try {
			const response = await fetch('/game_weeks', {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': window.csrfToken,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch game weeks: ${response.status}`);
			}

			const data = await response.json();

			if (!data || !Array.isArray(data.game_weeks)) {
				throw new Error('Invalid game weeks data received');
			}

			const date = new Date(data?.game_weeks[0]?.gameweek_start_date);
			const lastdate = data?.game_weeks.length - 1;
			const enddate = new Date(data?.game_weeks[lastdate]?.gameweek_end_date);

			const startyear = date.getFullYear();
			const endyear = enddate.getFullYear().toString().slice(-2);
			const startYear = startyear;
			const endYear = endyear;
			set({
				startYear,
				endYear
			})
		} catch (err) {
			devErrLog('Error in getGameWeeks:', err);
		}
	}
}))

export default useAuthStore